import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Box, Button, Heading } from '@zillow/constellation-10';
import styled from 'styled-components';
import { ROUTES } from '../../constants/tenant-screening-services';

import { ValuePropsSection } from '../../components/TenantScreeningServices/Landing/ValuePropsSection';
import { FAQSection } from '../../components/TenantScreeningServices/Landing/FAQSection';
import images from '../../images/ztss';
import { useNavigate } from '../../hooks';
import {
  emitZtssStartScreeningTenantsEvent,
  emitZtssViewApplicationsEvent,
  emitZtssViewLandingPageEvent,
} from '../../analytics/clickstreamEvents';
import { useAuth } from '@zg-rentals/auth-comet';

const StyledHeroImage = styled(Box)`
  background-image: url(${images.hero.src});
`;

export default function TenantScreeningServicesLandingPage() {
  const router = useRouter();
  const { user: userData } = useAuth();
  const { utm_source, utm_content } = router.query;
  const navigate = useNavigate();

  useEffect(() => {
    emitZtssViewLandingPageEvent(userData, {
      utm_source_nm: String(utm_source || ''),
      utm_content_nm: String(utm_content || ''),
    });
  }, [utm_source, utm_content, userData]);

  const handleStartScreening = () => {
    emitZtssStartScreeningTenantsEvent(userData);
    navigate(ROUTES.ENTER_YOUR_NAME);
  };

  const handleViewApplications = () => {
    emitZtssViewApplicationsEvent(userData);
    navigate(ROUTES.ADDRESSES);
  };

  return (
    <Box id="ztss-landing-page">
      <Box
        css={{
          position: 'relative',
          background: 'gray.100',
          maxWidth: 'none',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: {
            base: '400px',
            md: '481px',
            lg: '562px',
          },
        }}
      >
        <StyledHeroImage
          css={{
            position: 'absolute',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            width: '100%',
            zIndex: 0,
            top: 0,
            left: 0,
            backgroundPosition: 'center',
            filter: 'brightness(0.55)',
            height: {
              base: '100%',
              md: '481px',
              lg: '562px',
            },
          }}
        />
        <Box
          css={{
            zIndex: 1,
            position: 'relative',
            textAlign: 'center',
            maxWidth: '816px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'lg',
          }}
        >
          <Heading
            level={1}
            fontFamily="heading-alt"
            textStyle="responsive.heading-xl"
            css={{
              color: 'white',
              marginBottom: { base: 'sm', md: 'md' },
            }}
          >
            Save time with all-in-one tenant&nbsp;screening
          </Heading>

          <Heading
            level={2}
            textStyle="responsive.heading-sm"
            css={{
              color: 'white',
              marginBottom: { base: 'sm', md: 'md' },
              fontSize: { base: 'body-sm', md: 'body-lg' },
            }}
          >
            Zillow applications include residence history, background and credit checks, and eliminate the hassle of
            collecting fees.
          </Heading>

          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              width: '243px',
            }}
          >
            <Button
              emphasis="filled"
              tone="brand"
              css={{
                marginBottom: 'sm',
              }}
              onClick={handleStartScreening}
            >
              Start screening applicants
            </Button>
            <Button
              emphasis="outlined"
              tone="neutral"
              css={{
                marginBottom: 'sm',
              }}
              onClick={handleViewApplications}
            >
              View applications
            </Button>
          </Box>
        </Box>
      </Box>
      <ValuePropsSection />
      <FAQSection />
    </Box>
  );
}
